import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog, type ConfirmTone } from '../ui/ConfirmDialog';
import {
  ConfirmationContext,
  type ConfirmationContextValue,
  type ConfirmationOptions,
} from './ConfirmationContext';

type Pending = {
  options: ConfirmationOptions;
  resolve: (value: boolean) => void;
};

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);

  useEffect(() => {
    pendingRef.current = pending;
  });

  const confirm = useCallback((options: ConfirmationOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  const handleConfirm = useCallback((): void => {
    pendingRef.current?.resolve(true);
    setPending(null);
  }, []);

  const handleCancel = useCallback((): void => {
    pendingRef.current?.resolve(false);
    setPending(null);
  }, []);

  const value = useMemo<ConfirmationContextValue>(() => ({ confirm }), [confirm]);

  const tone: ConfirmTone =
    pending?.options.tone ?? (pending?.options.danger ? 'danger' : 'warning');

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        tone={tone}
        title={pending?.options.title ?? ''}
        description={pending?.options.description}
        confirmLabel={pending?.options.confirmLabel}
        cancelLabel={pending?.options.cancelLabel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmationContext.Provider>
  );
}
